import { EzStandardStateComponent } from '@template-ui/components/ez-standard-state/ez-standard-state.component';
import { HearingEnrollmentSetupComponent } from './hearing-enrollment-setup.component';
import { WorkflowFamilyVisibilityComponent, type WorkflowFamilyVisibilityState, type WorkflowFamilyVisibilityRequest } from './workflow-family-visibility.component';
import { WorkflowDraftConfirmationService } from './workflow-draft-confirmation.service';
import { UiButtonDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  Directive,
  EmbeddedViewRef,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  ElementRef,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ButtonCellRenderer } from '@app/shared/components/tables/ag-grid-cells/button-cell-renderer/button-cell-renderer.component';
import { RoleService } from '@app/shared/services/roles.service';
import { EzFormComponent } from '@template-ui/components/ez-form/ez-form.component';
import { UiLibraryModule } from '@template-ui/template-ui.module';
import { ButtonConfig, TabConfig } from '@template-ui/types';
import { EzFormInputType, InputConfig } from '@template-ui/types/ez-form.types';
import { ColDef } from 'ag-grid-community';
import { forkJoin, Subscription } from 'rxjs';
import {
  WorkflowDecisionRequirementDiagnostic,
  WorkflowDefinitionVersionDetail,
  WorkflowDefinitionVersionSummary,
  WorkflowAlertEvaluation,
  WorkflowAlertRule,
  WorkflowAlertRuleUpsertRequest,
  WorkflowDecisionRecord,
  WorkflowDecisionRecordPage,
  WorkflowRegistryDiagnosticResult,
  WorkflowRegistryDiagnosticDisplayError,
  WorkflowStepDefinitionDiagnostic,
  WorkflowTransitionDiagnostic,
  toWorkflowRegistryFailure,
  toWorkflowRegistrySuccess,
} from './platform-workflow-registry-diagnostics.models';
import {
  WorkflowDefinitionCatalogPage,
  WorkflowDefinitionCatalogQuery,
  WorkflowDefinitionLifecycleDetail,
  WorkflowDefinitionFamilyPage,
  WorkflowDefinitionFamilySummary,
  WorkflowDefinitionFamilyVisibility,
} from './platform-workflow-definition-lifecycle.models';
import {
  WorkflowFoundationExampleInfo,
  PlatformWorkflowDefinitionLifecycleService,
  createWorkflowDefinitionMutationIntent,
  type WorkflowDefinitionMutationIntent,
} from './platform-workflow-definition-lifecycle.service';
import {
  normalizeCatalogQuery,
  PlatformWorkflowRegistryCatalogComponent,
} from './platform-workflow-registry-catalog.component';
import {
  PlatformWorkflowDraftWorkspaceComponent,
  type WorkflowDraftWorkbenchState,
  type WorkflowPublishedDraftHandoff,
} from './platform-workflow-draft-workspace.component';
import {
  PlatformWorkflowVersionWorkspaceComponent,
  type WorkflowVersionWorkbenchView,
  type WorkflowPublishedDraftAction,
  type WorkflowPublishedDraftRequest,
  workflowPublishedDraftAction,
  workflowPublishedDraftSourceContext,
} from './platform-workflow-version-workspace.component';
import { PlatformWorkflowRegistryDiagnosticsService } from './platform-workflow-registry-diagnostics.service';
import type {
  WorkflowAlertPreviewFormValues,
  WorkflowAlertRuleFormValues,
  WorkflowDecisionRequirementRow,
  WorkflowRegistryDiagnosticsWorkspace,
} from './platform-workflow-registry-diagnostics-workspace';
const RECIPIENT_TYPE_OPTIONS = [
  { value: 'role', viewValue: 'Role' },
  { value: 'queue', viewValue: 'Queue' },
  { value: 'current_assignee', viewValue: 'Current Assignee' },
  { value: 'user', viewValue: 'Individual User' },
];

const SEVERITY_OPTIONS = [
  { value: 'info', viewValue: 'Info' },
  { value: 'warning', viewValue: 'Warning' },
  { value: 'critical', viewValue: 'Critical' },
];

const WORKFLOW_ALERT_RULE_MANAGE_PERMISSION = 'workflow_alert_rule.manage';
const WORKFLOW_RUNTIME_READ_PERMISSION = 'workflow_runtime.read';
const WORKFLOW_REGISTRY_FRAGMENT = 'workflow-registry-diagnostics';
const WORKFLOW_REGISTRY_ROUTE = '/platform-admin/workflow-registry-diagnostics';

export type WorkflowRegistryView = 'versions' | 'drafts' | 'version';

interface WorkflowDefinitionSelection {
  workflowCode: string;
  definitionVersionCode: string;
}
type PublishedDraftTarget = {
  readonly context: string;
  readonly action: WorkflowPublishedDraftAction;
  readonly actionContext: string;
  readonly actor: string;
  readonly generation: number;
} & (
  | {
      readonly kind: 'version';
      readonly source: WorkflowDefinitionVersionDetail;
      readonly editor: PlatformWorkflowVersionWorkspaceComponent;
      readonly request: WorkflowPublishedDraftRequest;
    }
  | {
      readonly kind: 'catalog';
      readonly source: WorkflowDefinitionLifecycleDetail;
    }
);
interface PublishedDraftFailure {
  readonly target: PublishedDraftTarget;
  readonly error: WorkflowRegistryDiagnosticDisplayError;
}

/** Hide a denied editor without discarding its unsaved form state. */
@Directive({ selector: '[workflowRegistryReadAccess]' })
export class WorkflowRegistryReadAccessDirective implements OnDestroy {
  private readonly template = inject(TemplateRef<unknown>);
  private readonly container = inject(ViewContainerRef);
  private view?: EmbeddedViewRef<unknown>;

  @Input()
  set workflowRegistryReadAccess(allowed: boolean) {
    if (allowed) {
      if (!this.view) {
        this.view = this.container.createEmbeddedView(this.template);
      } else if (this.container.indexOf(this.view) < 0) {
        this.container.insert(this.view);
      }
    } else if (this.view) {
      const index = this.container.indexOf(this.view);
      if (index >= 0) this.container.detach(index);
    }
  }

  ngOnDestroy(): void {
    if (this.view && !this.view.destroyed) this.view.destroy();
  }
}

@Component({
  selector: 'phms-platform-workflow-registry-diagnostics',
  imports: [
    HearingEnrollmentSetupComponent,
    WorkflowFamilyVisibilityComponent,
    UiButtonDirective,
    EzStandardStateComponent,
    WorkflowRegistryReadAccessDirective,
    CommonModule,
    UiLibraryModule,
    PlatformWorkflowRegistryCatalogComponent,
    PlatformWorkflowDraftWorkspaceComponent,
    PlatformWorkflowVersionWorkspaceComponent,
  ],
  templateUrl: './platform-workflow-registry-diagnostics.component.html',
  styleUrl: './platform-workflow-registry-diagnostics.component.scss',
})
export class PlatformWorkflowRegistryDiagnosticsComponent implements WorkflowRegistryDiagnosticsWorkspace {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly diagnosticsService = inject(
    PlatformWorkflowRegistryDiagnosticsService,
  );
  private readonly lifecycleService = inject(
    PlatformWorkflowDefinitionLifecycleService,
  );
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly roles = inject(RoleService);
  private readonly visibilityConfirmation = inject(WorkflowDraftConfirmationService);
  readonly visibilityDetails = signal<Record<string, WorkflowDefinitionFamilyVisibility>>({});
  readonly visibilityStates = signal<Record<string, WorkflowFamilyVisibilityState>>({});
  readonly visibilityMessage = signal<string | null>(null);
  private readonly visibilityReads = new Map<string, number>();
  private readonly visibilityStateOwners = new Map<string, string>();
  readonly visibilityViewStates = computed(() => Object.fromEntries(Object.keys(this.visibilityStates()).map(code => [code, this.familyVisibilityState(code)])));
  private readonly visibilityIntents = new Map<string, {
    detail: WorkflowDefinitionFamilyVisibility; isHidden: boolean; owner: string; intent: WorkflowDefinitionMutationIntent;
  }>();
  readonly selectedDefinitionKey = signal<WorkflowDefinitionSelection | null>(
    null,
  );
  private catalogLoadSubscription?: Subscription;
  private catalogLoadRequestId = 0;
  private detailLoadSubscription?: Subscription;
  private detailLoadRequestId = 0;
  private alertRulesLoadSubscription?: Subscription;
  private alertRulesLoadRequestId = 0;
  private alertPreviewSubscription?: Subscription;
  private alertPreviewRequestId = 0;
  private decisionRecordsLoadSubscription?: Subscription;
  private decisionRecordsLoadRequestId = 0;
  private routeStateInitialized = false;
  private shouldFocusVersionBackAfterLoad = false;
  private shouldFocusActivationAfterLoad = false;
  private readonly newlyPublishedVersion =
    signal<WorkflowDefinitionSelection | null>(null);
  private readonly publishedDraftGeneration = signal(0);
  private publishedDraftRouteContext = '';
  private readonly publishedDraftTarget = signal<PublishedDraftTarget | null>(
    null,
  );
  private readonly publishedDraftError = signal<PublishedDraftFailure | null>(
    null,
  );
  readonly publishedDraftFailure = computed(() => {
    const failure = this.publishedDraftError();
    return failure &&
      this.publishedDraftTarget() === failure.target &&
      this.publishedDraftCurrent(failure.target)
      ? failure
      : null;
  });
  readonly publishedDraftPending = computed(() => {
    const target = this.publishedDraftTarget();
    return target &&
      this.publishedDraftCurrent(target) &&
      this.isOpeningPublishedDraft
      ? target.source.definitionVersionCode
      : null;
  });
  get isOpeningPublishedDraft(): boolean {
    return this.draftWorkspace?.isRunningAuthoringAction() ?? false;
  }

  @ViewChild('alertRuleForm')
  private alertRuleForm?: EzFormComponent;

  @ViewChild('alertPreviewForm')
  private alertPreviewForm?: EzFormComponent;

  private retainedDraftWorkspace?: PlatformWorkflowDraftWorkspaceComponent;

  @ViewChild(PlatformWorkflowDraftWorkspaceComponent)
  private set draftWorkspace(
    value: PlatformWorkflowDraftWorkspaceComponent | undefined,
  ) {
    // A denied read detaches the view; route guards must still protect its buffer.
    if (value) this.retainedDraftWorkspace = value;
  }

  private get draftWorkspace():
    | PlatformWorkflowDraftWorkspaceComponent
    | undefined {
    return this.retainedDraftWorkspace;
  }

  @ViewChild('registryTabs', { read: ElementRef })
  private registryTabs?: ElementRef<HTMLElement>;

  @ViewChild(PlatformWorkflowVersionWorkspaceComponent)
  private versionWorkspace?: PlatformWorkflowVersionWorkspaceComponent;

  @ViewChild('detailLoadingBackButton')
  private detailLoadingBackButton?: ElementRef<HTMLButtonElement>;

  readonly registryAccessDenied = signal(false);
  readonly localExample = signal<WorkflowFoundationExampleInfo | null>(null);
  readonly isLoadingList = signal(false);
  readonly canManageAlertRules = this.roles.hasAnyPermissionSignal(
    WORKFLOW_ALERT_RULE_MANAGE_PERMISSION,
  );
  readonly canReadWorkflowRuntime = this.roles.hasAnyPermissionSignal(
    WORKFLOW_RUNTIME_READ_PERMISSION,
  );
  readonly isLoadingDetail = signal(false);
  readonly isLoadingAlertRules = signal(false);
  readonly isLoadingDecisionRecords = signal(false);
  readonly isSavingAlertRule = signal(false);
  readonly isPreviewingAlertRules = signal(false);
  readonly draftWorkbenchState = signal<WorkflowDraftWorkbenchState | null>(
    null,
  );
  readonly registryView = signal<WorkflowRegistryView>('versions');
  readonly activeVersionView = signal<WorkflowVersionWorkbenchView>('map');
  readonly catalogQuery = signal<WorkflowDefinitionCatalogQuery>({
    pageNumber: 1,
    pageSize: 25,
  });
  readonly catalogPage = signal<WorkflowDefinitionCatalogPage | null>(null);
  readonly familyPage = signal<WorkflowDefinitionFamilyPage | null>(null);
  readonly selectedFamily = signal<WorkflowDefinitionFamilySummary | null>(null);
  private readonly familyError = signal<WorkflowRegistryDiagnosticDisplayError | null>(null);
  private familyBrowseQuery: WorkflowDefinitionCatalogQuery = { pageNumber: 1, pageSize: 25 };
  readonly templatesOpen = signal(false);
  readonly registryNavigationTabs = [
    { id: 'versions', label: 'Versions', controlId: 'workflow-registry-versions-view' },
    { id: 'drafts', label: 'Drafts', controlId: 'workflow-registry-drafts-view' },
  ];
  onRegistryTabRequested(view: string): void {
    if (view === 'drafts' && this.draftWorkspace) {
      const workspace = this.draftWorkspace;
      workspace.requestExternalSelectionChange(() => {
        workspace.clearDraftDiscoveryScope(); this.showRegistryView('drafts');
      }, () => this.restoreExternalRegistryContextUrl());
    } else if (view === 'versions' || view === 'drafts') this.showRegistryView(view);
  }
  readonly listResult =
    signal<WorkflowRegistryDiagnosticResult<WorkflowDefinitionCatalogPage> | null>(
      null,
    );
  readonly detailResult =
    signal<WorkflowRegistryDiagnosticResult<WorkflowDefinitionVersionDetail> | null>(
      null,
    );
  readonly alertRulesResult = signal<WorkflowRegistryDiagnosticResult<
    WorkflowAlertRule[]
  > | null>(null);
  readonly alertPreviewResult = signal<WorkflowRegistryDiagnosticResult<
    WorkflowAlertEvaluation[]
  > | null>(null);
  readonly decisionRecordsResult =
    signal<WorkflowRegistryDiagnosticResult<WorkflowDecisionRecordPage> | null>(
      null,
    );

  readonly definitions = computed(() => this.catalogPage()?.items ?? []);
  readonly catalogError = computed(() => {
    if (this.registryAccessDenied())
      return { title: 'Access denied', messages: [], statusCode: 403 };
    if (!this.catalogQuery().workflowCode) return this.familyError();
    const result = this.listResult();
    return result && result.state !== 'resolved' ? result.error : null;
  });
  readonly selectedDefinition = computed(() => {
    const result = this.detailResult();
    return !this.registryAccessDenied() && result?.state === 'resolved'
      ? result.data
      : null;
  });
  readonly detailReadState = computed(() => {
    const result = this.detailResult();
    return this.registryAccessDenied() ||
      result?.state === 'forbidden' ||
      result?.error?.statusCode === 403
      ? 'forbidden'
      : 'error';
  });
  readonly detailReadMessage = computed(() =>
    this.detailReadState() === 'forbidden'
      ? 'You do not have permission to view this workflow definition. Contact an administrator if you need access.'
      : 'The selected workflow definition could not be loaded. Try again.',
  );
  readonly isNewlyPublishedVersion = computed(() => {
    const published = this.newlyPublishedVersion();
    const selected = this.selectedDefinition();
    return (
      !!published &&
      published.workflowCode === selected?.workflowCode &&
      published.definitionVersionCode === selected.definitionVersionCode
    );
  });
  readonly alertRules = computed(() => this.alertRulesResult()?.data ?? []);
  readonly alertEvaluations = computed(
    () => this.alertPreviewResult()?.data ?? [],
  );
  readonly decisionRecordPage = computed(
    () => this.decisionRecordsResult()?.data ?? null,
  );
  readonly decisionRecords = computed(
    () => this.decisionRecordPage()?.items ?? [],
  );
  readonly workflowTransitions = computed(
    () => this.selectedDefinition()?.workflowTransitions ?? [],
  );
  readonly stepDefinitions = computed(
    () => this.selectedDefinition()?.stepDefinitions ?? [],
  );
  readonly requiredDecisions = computed(
    () => this.selectedDefinition()?.requiredDecisions ?? [],
  );
  readonly decisionRequirementRows = computed(() =>
    this.buildDecisionRequirementRows(),
  );
  readonly decisionRequirementCount = computed(
    () => this.decisionRequirementRows().length,
  );
  readonly isDraftWorkbenchOpen = computed(
    () => this.draftWorkbenchState() !== null,
  );
  readonly isVersionCatalogVisible = computed(
    () => !this.isDraftWorkbenchOpen() && this.registryView() === 'versions',
  );
  readonly isDraftCatalogVisible = computed(
    () => this.isDraftWorkbenchOpen() || this.registryView() === 'drafts',
  );
  readonly isVersionDetailVisible = computed(
    () => !this.isDraftWorkbenchOpen() && this.registryView() === 'version',
  );
  readonly isRegistryBrowseVisible = computed(
    () => !this.isDraftWorkbenchOpen() && this.registryView() !== 'version',
  );
  readonly registryPanelTitle = computed(() =>
    this.registryAccessDenied()
      ? 'Workflow registry'
      : (this.draftWorkbenchState()?.draftCode ?? 'Workflow registry'),
  );
  readonly registryPanelSubtitle = computed(() => {
    if (this.registryAccessDenied())
      return 'Workflow registry access is unavailable.';
    const workbench = this.draftWorkbenchState();
    if (workbench) {
      if (workbench.lifecycleStatusCode === 'archived')
        return `Archived source for ${workbench.workflowCode} · saved revision ${workbench.revision} · read only`;
      return `Working draft for ${workbench.workflowCode} · saved revision ${workbench.revision}`;
    }

    switch (this.registryView()) {
      case 'drafts':
        return 'Review saved drafts and continue work in progress.';
      case 'version': {
        const selection = this.selectedDefinitionKey();
        if (this.detailReadState() === 'forbidden') {
          return 'Review a published workflow version.';
        }
        return selection
          ? `Reviewing ${this.selectedDefinition()?.displayName || 'selected workflow'} · version ${selection.definitionVersionCode}.`
          : 'Review a published workflow version.';
      }
      default:
        return 'Review workflow versions or continue work on a draft.';
    }
  });

  readonly alertRuleFormConfig: InputConfig[] = this.buildAlertRuleFormConfig();
  readonly alertPreviewFormConfig: InputConfig[] =
    this.buildAlertPreviewFormConfig();
  readonly definitionColumnDefs: ColDef[] = this.buildDefinitionColumnDefs();
  readonly alertRuleColumnDefs: ColDef[] = this.buildAlertRuleColumnDefs();
  readonly alertEvaluationColumnDefs: ColDef[] =
    this.buildAlertEvaluationColumnDefs();
  readonly workflowTransitionColumnDefs: ColDef[] =
    this.buildWorkflowTransitionColumnDefs();
  readonly stepColumnDefs: ColDef[] = this.buildStepColumnDefs();
  readonly decisionColumnDefs: ColDef[] = this.buildDecisionColumnDefs();
  readonly decisionRequirementColumnDefs: ColDef[] =
    this.buildDecisionRequirementColumnDefs();
  readonly decisionRecordColumnDefs: ColDef[] =
    this.buildDecisionRecordColumnDefs();
  readonly workflowTabs: TabConfig[] = [
    {
      label: 'Runtime records',
      fragment: 'workflow-registry-runtime-records',
      hidden: computed(() => !this.canReadWorkflowRuntime()),
      loadComponent: () =>
        import('./platform-workflow-registry-diagnostics-tabs.component').then(
          (module) => module.PlatformWorkflowRegistryRuntimeRecordsTabComponent,
        ),
      inputs: { workspace: this },
    },
    {
      label: 'Alert rules',
      fragment: 'workflow-registry-alert-rules',
      hidden: computed(() => !this.canReadWorkflowRuntime()),
      loadComponent: () =>
        import('./platform-workflow-registry-diagnostics-tabs.component').then(
          (module) => module.PlatformWorkflowRegistryAlertsTabComponent,
        ),
      inputs: { workspace: this },
    },
    {
      label: 'Registry evidence',
      fragment: 'workflow-registry-evidence',
      loadComponent: () =>
        import('./platform-workflow-registry-diagnostics-tabs.component').then(
          (module) => module.PlatformWorkflowRegistryDiagnosticsTabComponent,
        ),
      inputs: { workspace: this },
    },
  ];

  readonly alertRuleActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: 'saveAlertRule',
      buttonText: this.isSavingAlertRule() ? 'Saving...' : 'Save Rule',
      disabled: this.isSavingAlertRule() || this.isLoadingAlertRules(),
      hidden: !this.canManageAlertRules(),
    },
    {
      id: 'reloadAlertRules',
      buttonText: this.isLoadingAlertRules() ? 'Loading...' : 'Reload',
      severity: 'secondary',
      disabled: this.isSavingAlertRule() || this.isLoadingAlertRules(),
    },
  ]);

  readonly alertPreviewActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: 'previewAlertRules',
      buttonText: this.isPreviewingAlertRules() ? 'Previewing...' : 'Preview',
      disabled: this.isPreviewingAlertRules(),
    },
  ]);

  onDraftWorkbenchStateChange(state: WorkflowDraftWorkbenchState | null): void {
    this.draftWorkbenchState.set(state);
    if (state) { this.registryView.set('drafts'); queueMicrotask(() => this.refreshFamilyVisibility(state.workflowCode, false)); }
  }

  onEditableDraftOpened(source: WorkflowDefinitionSelection): void {
    this.publishedDraftTarget.set(null);
    this.publishedDraftError.set(null);
    this.searchDefinitions(this.catalogQuery());
    if (this.selectedDefinitionKey()?.workflowCode === source.workflowCode)
      this.retrySelectedDefinition();
  }

  alertRuleFormValues: WorkflowAlertRuleFormValues = {
    ruleCode: 'post-hearing-review-alert',
    displayName: 'Post-Hearing Review Alert',
    eventTypeCode: 'phms.hearing.completed',
    workflowCode: 'phms_adjudication',
    recipientTargetTypeCode: 'role',
    recipientUserId: null,
    recipientTargetKey: 'operations_manager',
    alertTitle: 'Post-Hearing Review Needed',
    alertMessageTemplate:
      'Workflow {workflowInstanceId} needs post-hearing review.',
    severityCode: 'warning',
    isActive: true,
  };

  alertPreviewFormValues: WorkflowAlertPreviewFormValues = {
    eventTypeCode: 'phms.hearing.completed',
    workflowInstanceId: null,
    correlationKey: '',
    workflowCode: 'phms_adjudication',
  };

  openLocalExample(): void {
    const example = this.localExample();
    if (!example?.draftId || !this.isRegistryBrowseVisible()) return;
    void this.router.navigate([WORKFLOW_REGISTRY_ROUTE], {
      queryParams: { registryView: 'drafts', draft: example.draftId },
    });
  }

  constructor() {
    this.lifecycleService
      .getLocalExample?.()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (value) => this.localExample.set(value),
        error: () => this.localExample.set(null),
      });
    if (this.redirectToCanonicalWorkflowRegistryRoute()) {
      return;
    }

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((queryParams) => {
        const scopedDraft = queryParams.get('draftWorkflow')?.trim();
        if (scopedDraft) queueMicrotask(() => this.refreshFamilyVisibility(scopedDraft, false));
        const nextSelection = readWorkflowSelection(
          queryParams.get('workflow'),
          queryParams.get('version'),
        );
        const nextRegistryView = readRegistryView(queryParams, nextSelection);
        const draftRouteContext = JSON.stringify([
          nextSelection,
          nextRegistryView,
          readVersionWorkbenchView(queryParams),
        ]);
        if (draftRouteContext !== this.publishedDraftRouteContext) {
          this.publishedDraftRouteContext = draftRouteContext;
          this.publishedDraftGeneration.update((generation) => generation + 1);
        }
        this.activeVersionView.set(readVersionWorkbenchView(queryParams));
        if (nextSelection && !this.isSelectionCurrent(nextSelection)) {
          this.requestDefinitionSelection(
            nextSelection,
            false,
            () => this.restoreExternalRegistryContextUrl(),
            nextRegistryView,
          );
        } else if (nextRegistryView !== this.registryView()) {
          const applyView = () => {
            this.registryView.set(nextRegistryView);
            if (
              nextRegistryView !== 'drafts' &&
              readPositiveInteger(queryParams.get('draft')) !== null
            ) {
              this.writeRegistryViewToUrl(nextRegistryView);
            }
          };
          if (this.routeStateInitialized && this.draftWorkspace) {
            this.draftWorkspace.requestExternalSelectionChange(applyView, () =>
              this.restoreExternalRegistryContextUrl(),
            );
          } else {
            applyView();
          }
        }

        const nextCatalogQuery = readCatalogQuery(queryParams);
        const catalogQueryChanged = !catalogQueriesEqual(
          nextCatalogQuery,
          this.catalogQuery(),
        );
        this.catalogQuery.set(nextCatalogQuery);
        if (this.routeStateInitialized && catalogQueryChanged) {
          this.searchDefinitions(nextCatalogQuery);
        }
      });

    this.routeStateInitialized = true;
    this.searchDefinitions(this.catalogQuery());
  }

  private redirectToCanonicalWorkflowRegistryRoute(): boolean {
    const hostTabFragment = this.route.snapshot.data['adminTabFragment'] as
      | string
      | undefined;
    const currentFragment = this.route.snapshot.fragment;
    const isWorkflowRegistryFragment =
      currentFragment === WORKFLOW_REGISTRY_FRAGMENT ||
      currentFragment?.startsWith(`${WORKFLOW_REGISTRY_FRAGMENT}-`) === true ||
      currentFragment?.startsWith(`${WORKFLOW_REGISTRY_FRAGMENT}/`) === true;

    if (
      !hostTabFragment ||
      hostTabFragment === WORKFLOW_REGISTRY_FRAGMENT ||
      !isWorkflowRegistryFragment
    ) {
      return false;
    }

    void this.router.navigate([WORKFLOW_REGISTRY_ROUTE], {
      queryParams: this.route.snapshot.queryParams,
      fragment: currentFragment,
      replaceUrl: true,
    });
    return true;
  }

  onAlertRuleActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case 'saveAlertRule':
        this.saveAlertRule();
        break;
      case 'reloadAlertRules':
        this.loadAlertRules();
        break;
      default:
        break;
    }
  }

  onAlertPreviewActionButtonClick(button: ButtonConfig): void {
    if (button.id === 'previewAlertRules') {
      this.previewAlertRules();
    }
  }

  searchDefinitions(
    query: WorkflowDefinitionCatalogQuery = this.catalogQuery(),
    updateUrl = false,
  ): void {
    const normalizedQuery = normalizeCatalogQuery(query);
    if (this.catalogQuery().workflowCode !== normalizedQuery.workflowCode) {
      this.catalogPage.set(null); this.listResult.set(null);
    }
    this.catalogQuery.set(normalizedQuery);
    if (updateUrl) {
      this.writeCatalogQueryToUrl(normalizedQuery);
    }

    if (!normalizedQuery.workflowCode) {
      this.familyBrowseQuery = normalizedQuery;
      this.searchFamilies(normalizedQuery);
      return;
    }

    this.isLoadingList.set(true);
    if (this.selectedFamily()?.workflowCode !== normalizedQuery.workflowCode) this.selectedFamily.set(null);
    this.catalogLoadSubscription?.unsubscribe();
    const requestId = ++this.catalogLoadRequestId;

    this.catalogLoadSubscription = forkJoin({ page: this.lifecycleService.getCatalog(normalizedQuery),
      families: this.lifecycleService.getFamilies({ workflowCode: normalizedQuery.workflowCode, includeHidden: true, pageNumber: 1, pageSize: 10 }) })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ page, families }) => {
          if (requestId !== this.catalogLoadRequestId) {
            return;
          }

          this.registryAccessDenied.set(false);
          this.draftWorkspace?.resumeConfirmedRegistryReadAccess();
          this.catalogPage.set(page);
          const matches = families.items.filter(row => row.workflowCode === normalizedQuery.workflowCode);
          this.selectedFamily.set(matches.length === 1 ? matches[0] : null);
          this.adoptFamilyVisibility(families);
          this.listResult.set(toWorkflowRegistrySuccess(page));
          this.isLoadingList.set(false);
        },
        error: (error: unknown) => {
          if (requestId !== this.catalogLoadRequestId) {
            return;
          }

          const failure =
            toWorkflowRegistryFailure<WorkflowDefinitionCatalogPage>(error);
          this.listResult.set(failure);
          if (failure.state === 'forbidden') {
            this.invalidateVisibilityReads();
            this.draftWorkspace?.suspendRegistryReadAccess(
              failure.error?.statusCode === 401 ? 401 : 403,
            );
            this.registryAccessDenied.set(true);
            this.catalogPage.set(null);
            this.localExample.set(null);
            this.detailLoadSubscription?.unsubscribe();
            this.detailLoadRequestId++;
            this.detailResult.set({
              state: 'forbidden',
              data: null,
              error: failure.error,
            });
            this.isLoadingDetail.set(false);
            this.resetDefinitionDependencies();
          }
          this.isLoadingList.set(false);
        },
      });
  }

  onCatalogQueryChange(query: WorkflowDefinitionCatalogQuery): void {
    this.searchDefinitions(query, true);
  }

  private searchFamilies(query: WorkflowDefinitionCatalogQuery): void {
    this.isLoadingList.set(true); this.catalogLoadSubscription?.unsubscribe();
    const request = ++this.catalogLoadRequestId;
    this.catalogLoadSubscription = this.lifecycleService.getFamilies({ search: query.search, includeHidden: query.includeHidden === 'true', pageNumber: query.pageNumber, pageSize: query.pageSize })
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: page => {
          if (request !== this.catalogLoadRequestId) return;
          this.familyPage.set(page); this.familyError.set(null); this.registryAccessDenied.set(false);
          this.adoptFamilyVisibility(page);
          this.draftWorkspace?.resumeConfirmedRegistryReadAccess(); this.isLoadingList.set(false);
        },
        error: error => {
          if (request !== this.catalogLoadRequestId) return;
          const failure = toWorkflowRegistryFailure<WorkflowDefinitionFamilyPage>(error);
          this.familyError.set(failure.error); this.isLoadingList.set(false);
          if (failure.state === 'forbidden') {
            this.invalidateVisibilityReads();
            this.familyPage.set(null); this.catalogPage.set(null); this.registryAccessDenied.set(true);
            this.localExample.set(null);
            this.draftWorkspace?.suspendRegistryReadAccess(failure.error?.statusCode === 401 ? 401 : 403);
            this.detailLoadSubscription?.unsubscribe(); this.detailLoadRequestId++;
            this.detailResult.set({ state: 'forbidden', data: null, error: failure.error });
            this.isLoadingDetail.set(false); this.resetDefinitionDependencies();
          }
        },
      });
  }

  openFamily(family: WorkflowDefinitionFamilySummary): void {
    if (!this.isVersionCatalogVisible() || this.catalogQuery().workflowCode || this.isLoadingList() || this.registryAccessDenied() || this.familyError() || !this.familyPage()?.items.includes(family)) return;
    const open = () => {
      if (!this.isVersionCatalogVisible() || this.catalogQuery().workflowCode || this.isLoadingList() || this.registryAccessDenied() || this.familyError() || !this.familyPage()?.items.includes(family)) return;
      this.selectedFamily.set(family);
      this.searchDefinitions({ workflowCode: family.workflowCode, pageNumber: 1, pageSize: this.catalogQuery().pageSize }, true);
    };
    if (this.draftWorkspace) this.draftWorkspace.requestExternalSelectionChange(open, () => this.restoreExternalRegistryContextUrl());
    else open();
  }

  showAllFamilies(): void {
    if (!this.isVersionCatalogVisible() || !this.catalogQuery().workflowCode) return;
    const open = () => { this.selectedFamily.set(null); this.searchDefinitions(this.familyBrowseQuery, true); };
    if (this.draftWorkspace) this.draftWorkspace.requestExternalSelectionChange(open, () => this.restoreExternalRegistryContextUrl());
    else open();
  }

  openFamilyDrafts(workflowCode: string, archived = false): void {
    const current = () => this.isVersionCatalogVisible() && !this.isLoadingList() && !this.registryAccessDenied() &&
      (this.catalogQuery().workflowCode ? !this.catalogError() && this.catalogQuery().workflowCode === workflowCode
        : !this.familyError() && this.familyPage()?.items.some(row => row.workflowCode === workflowCode));
    if (!current()) return;
    const workspace = this.draftWorkspace;
    if (!workspace) return;
    workspace.requestExternalSelectionChange(() => {
      if (!current()) return;
      workspace.openWorkflowDrafts(workflowCode, undefined, archived);
      this.registryView.set('drafts'); this.writeRegistryViewToUrl('drafts');
      this.refreshFamilyVisibility(workflowCode, false);
    }, () => this.restoreExternalRegistryContextUrl());
  }

  private visibilityOwner(): string {
    return JSON.stringify([this.roles.accessProfile?.()?.actor?.actorId ?? null, this.canCreateWorkflow()]);
  }
  draftFamilyCode(): string | null {
    return this.draftWorkbenchState()?.workflowCode ?? this.draftWorkspace?.draftQuery().workflowCode ?? null;
  }
  familyVisibility(code: string | null): WorkflowDefinitionFamilyVisibility | null {
    return code ? this.visibilityDetails()[code] ?? null : null;
  }
  familyVisibilityState(code: string | null): WorkflowFamilyVisibilityState {
    const state = code ? this.visibilityStates()[code] : null;
    if (state && code && this.visibilityStateOwners.get(code) !== this.visibilityOwner())
      return { busy: false, blocked: true, retry: false, message: 'The author context changed. Refresh shared visibility before continuing.' };
    return state ?? { busy: false, blocked: false, retry: false, message: null };
  }
  private visibilityContextCurrent(code: string): boolean {
    return !this.registryAccessDenied() && (this.isVersionCatalogVisible()
      ? this.catalogQuery().workflowCode ? this.catalogQuery().workflowCode === code : this.familyPage()?.items.some(row => row.workflowCode === code) === true
      : this.isDraftCatalogVisible() ? this.draftFamilyCode() === code : this.isVersionDetailVisible() && this.selectedDefinition()?.workflowCode === code);
  }
  private visibilityContext(code: string): string {
    return JSON.stringify([this.visibilityOwner(), this.registryView(), this.catalogQuery().workflowCode ?? null, this.draftFamilyCode(), code]);
  }
  readonly sharedVisibilityContext = (code: string) => this.visibilityContext(code);
  private setVisibilityState(code: string, state: WorkflowFamilyVisibilityState): void {
    this.visibilityStateOwners.set(code, this.visibilityOwner());
    this.visibilityStates.update(states => ({ ...states, [code]: state }));
  }
  private invalidateVisibilityReads(): void {
    for (const [code, request] of this.visibilityReads) this.visibilityReads.set(code, request + 1);
    this.visibilityIntents.clear();
    this.visibilityDetails.set({}); this.visibilityStates.set({}); this.visibilityStateOwners.clear();
  }
  private adoptFamilyVisibility(page: WorkflowDefinitionFamilyPage): void {
    this.visibilityDetails.update(details => {
      const next = { ...details };
      for (const row of page.items) {
        if (row.visibility?.workflowCode === row.workflowCode) next[row.workflowCode] = row.visibility;
        else delete next[row.workflowCode];
      }
      return next;
    });
  }
  private visibilityActionAvailable(code: string): boolean {
    return this.visibilityDetails()[code]?.isAvailable === true &&
      (!this.isVersionCatalogVisible() || !!this.catalogQuery().workflowCode || this.familyPage()?.sharedVisibilityAvailable === true);
  }
  refreshFamilyVisibility(code: string, explicit = true): void {
    if (!this.visibilityContextCurrent(code) || this.familyVisibilityState(code).busy || !explicit && this.visibilityDetails()[code]) return;
    const owner = this.visibilityOwner(), request = (this.visibilityReads.get(code) ?? 0) + 1;
    this.visibilityReads.set(code, request);
    this.setVisibilityState(code, { busy: true, blocked: true, retry: false, message: null });
    this.lifecycleService.getFamilyVisibility(code).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: detail => {
        if (request !== this.visibilityReads.get(code) || owner !== this.visibilityOwner()) return;
        if (detail.workflowCode !== code) { this.setVisibilityState(code, { busy: false, blocked: true, retry: false, message: 'The visibility response identified a different workflow. Refresh before trying again.' }); return; }
        this.visibilityDetails.update(details => ({ ...details, [code]: detail }));
        if (explicit) this.visibilityIntents.delete(code);
        this.setVisibilityState(code, { busy: false, blocked: false, retry: false, message: null });
      },
      error: error => {
        if (request !== this.visibilityReads.get(code) || owner !== this.visibilityOwner()) return;
        const failure = toWorkflowRegistryFailure(error).error;
        if (failure?.statusCode === 401 || failure?.statusCode === 403) {
          this.draftWorkspace?.suspendRegistryReadAccess(failure.statusCode);
          this.onDraftReadAccessDenied();
          return;
        }
        this.setVisibilityState(code, { busy: false, blocked: true, retry: false,
          message: failure?.statusCode === 404 ? 'This workflow is no longer available. Refresh the registry.'
            : failure?.statusCode === 401 || failure?.statusCode === 403 ? 'Shared visibility could not be read with your current access.' : 'Shared visibility could not be refreshed. Try again.' });
      },
    });
  }
  async requestFamilyVisibility(event: WorkflowFamilyVisibilityRequest): Promise<void> {
    const detail = event?.detail, code = detail?.workflowCode;
    if (!code || event.context !== this.visibilityContext(code) || !this.canCreateWorkflow() || !this.visibilityContextCurrent(code) || this.visibilityDetails()[code] !== detail ||
      this.familyVisibilityState(code).busy || this.familyVisibilityState(code).blocked || !this.visibilityActionAvailable(code) ||
      typeof detail.isHidden !== 'boolean' || typeof event.isHidden !== 'boolean' || event.isHidden === detail.isHidden || !Number.isInteger(detail.revision) || Number(detail.revision) < 0 || !detail.eTag) return;
    const context = this.visibilityContext(code), before = JSON.stringify(detail);
    this.setVisibilityState(code, { busy: true, blocked: true, retry: false, message: null });
    const confirmed = await this.visibilityConfirmation.confirm({ heading: event.isHidden ? 'Hide workflow for all authors?' : 'Restore workflow?',
      message: `${code} will ${event.isHidden ? 'be omitted from default Versions and Drafts discovery for all authors' : 'appear in normal discovery for all authors'}. Open drafts, availability, running work, access and history remain unchanged.`,
      confirmText: event.isHidden ? 'Hide for all authors' : 'Restore', cancelText: 'Cancel' });
    if (!confirmed || this.destroyRef.destroyed || context !== this.visibilityContext(code) || !this.visibilityContextCurrent(code) ||
      before !== JSON.stringify(this.visibilityDetails()[code]) || !this.canCreateWorkflow() || !this.visibilityActionAvailable(code)) {
      this.setVisibilityState(code, { busy: false, blocked: false, retry: false, message: null }); return;
    }
    this.visibilityIntents.set(code, { detail: structuredClone(detail), isHidden: event.isHidden, owner: this.visibilityOwner(), intent: createWorkflowDefinitionMutationIntent() });
    this.performFamilyVisibility(code);
  }
  retryFamilyVisibility(code: string): void {
    if (!this.familyVisibilityState(code).retry || this.familyVisibilityState(code).busy) return;
    this.performFamilyVisibility(code);
  }
  private performFamilyVisibility(code: string): void {
    const operation = this.visibilityIntents.get(code);
    if (!operation || operation.owner !== this.visibilityOwner() || !this.canCreateWorkflow() || !this.visibilityContextCurrent(code) || !this.visibilityActionAvailable(code)) return;
    this.setVisibilityState(code, { busy: true, blocked: true, retry: false, message: null });
    this.lifecycleService.updateFamilyVisibility(code, operation.detail.eTag!, { isHidden: operation.isHidden, expectedRevision: operation.detail.revision! }, operation.intent)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: detail => {
          if (this.registryAccessDenied() || this.visibilityIntents.get(code) !== operation || operation.owner !== this.visibilityOwner()) return;
          if (detail.workflowCode !== code || !detail.isAvailable || detail.isHidden !== operation.isHidden || !detail.eTag || !Number.isInteger(detail.revision)) {
            this.setVisibilityState(code, { busy: false, blocked: true, retry: true, message: 'The result could not be confirmed. Retry the same visibility change or refresh its state.' }); return;
          }
          this.visibilityIntents.delete(code); this.visibilityDetails.update(details => ({ ...details, [code]: detail }));
          this.setVisibilityState(code, { busy: false, blocked: false, retry: false, message: null });
          this.visibilityMessage.set(`${code} ${detail.isHidden ? 'is hidden' : 'was restored'} for all authors.`);
          this.searchDefinitions(this.catalogQuery()); this.draftWorkspace?.refreshDraftDiscovery();
        },
        error: error => {
          if (this.registryAccessDenied() || this.visibilityIntents.get(code) !== operation || operation.owner !== this.visibilityOwner()) return;
          const status = toWorkflowRegistryFailure(error).error?.statusCode;
          const uncertain = !status || status >= 500;
          this.setVisibilityState(code, { busy: false, blocked: true, retry: uncertain,
            message: status === 412 ? 'Shared visibility changed elsewhere. Refresh visibility before choosing a new change.'
              : status === 401 || status === 403 ? 'Your current access does not allow this visibility change. Check access and refresh.'
                : status === 404 ? 'This workflow is no longer available. Refresh the registry.'
                  : status === 409 ? 'Shared visibility is unavailable. Refresh its state.'
                    : uncertain ? 'The outcome is uncertain. Retry uses the same confirmed change, or refresh its state.' : 'Visibility was not changed. Refresh its state before trying again.' });
        },
      });
  }

  onDraftReadAccessDenied(): void {
    // The child has already invalidated its reads before reporting this denial.
    this.invalidateVisibilityReads();
    this.catalogLoadSubscription?.unsubscribe();
    this.catalogLoadRequestId++;
    this.detailLoadSubscription?.unsubscribe();
    this.detailLoadRequestId++;
    this.registryAccessDenied.set(true);
    this.catalogPage.set(null);
    this.familyPage.set(null);
    this.localExample.set(null);
    this.detailResult.set({ state: 'forbidden', data: null, error: null });
    this.isLoadingList.set(false);
    this.isLoadingDetail.set(false);
    this.resetDefinitionDependencies();
  }

  retryCatalog(): void {
    this.searchDefinitions(this.catalogQuery());
  }

  selectDefinition(
    summary:
      | WorkflowDefinitionVersionSummary
      | WorkflowDefinitionLifecycleDetail,
    updateUrl = true,
  ): void {
    const selection = toWorkflowSelection(summary);
    this.requestDefinitionSelection(selection, updateUrl, undefined, 'version');
  }

  showRegistryView(
    view: Exclude<WorkflowRegistryView, 'version'>,
    moveFocus = false,
  ): void {
    if (view === this.registryView() && !this.isVersionDetailVisible()) {
      return;
    }

    const applyView = () => {
      this.publishedDraftGeneration.update((generation) => generation + 1);
      this.shouldFocusVersionBackAfterLoad = false;
      this.shouldFocusActivationAfterLoad = false;
      this.newlyPublishedVersion.set(null);
      this.registryView.set(view);
      this.writeRegistryViewToUrl(view);
      if (moveFocus && view === 'versions') {
        this.focusAfterRender(() => {
          const tab = this.registryTabs?.nativeElement.querySelector<HTMLElement>('#workflow-registry-versions-view');
          return tab ? new ElementRef(tab) : undefined;
        });
      }
    };
    const restoreView = () => this.restoreExternalRegistryContextUrl();

    if (this.draftWorkspace) {
      this.draftWorkspace.requestExternalSelectionChange(
        applyView,
        restoreView,
      );
      return;
    }

    applyView();
  }

  backToRegistry(): void {
    this.shouldFocusVersionBackAfterLoad = false;
    this.showRegistryView('versions', true);
  }
  readonly canCreateWorkflow = this.roles.hasAnyPermissionSignal(
    'workflow_definition.author',
  );
  readonly canRunEligibilityTests = this.roles.hasAnyPermissionSignal('phms.hearing_eligibility_test.execute');
  beginCreateWorkflow(): void {
    const workspace = this.draftWorkspace;
    if (
      !workspace ||
      !workspace.canAuthor() ||
      workspace.isDraftEditorInteractionBlocked()
    )
      return;
    workspace.beginCreateDraft();
    if (workspace.authoringMode() === 'create') {
      this.registryView.set('drafts');
      this.writeRegistryViewToUrl('drafts');
    }
  }

  onVersionViewChange(view: WorkflowVersionWorkbenchView): void {
    if (view !== this.activeVersionView())
      this.publishedDraftGeneration.update((generation) => generation + 1);
    this.activeVersionView.set(view);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        versionView: view === 'map' ? null : view,
      },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  editSelectedWorkflow(): void {
    this.versionWorkspace?.requestEditWorkflow();
  }
  onVersionDraftRequested(
    editor: PlatformWorkflowVersionWorkspaceComponent,
    request: WorkflowPublishedDraftRequest,
  ): void {
    const definition = this.selectedDefinition(),
      action = workflowPublishedDraftAction(definition?.draftActionCode);
    if (
      editor !== this.versionWorkspace ||
      !definition ||
      !action ||
      !editor.isCurrentDraftEditRequest(request, definition) ||
      !this.publishedDraftNativeCurrent()
    )
      return;
    const target: PublishedDraftTarget = {
      kind: 'version',
      source: definition,
      editor,
      request: structuredClone(request),
      action,
      context: workflowPublishedDraftSourceContext(definition),
      actionContext: this.publishedDraftActionContext(definition),
      actor: this.publishedDraftActor(),
      generation: this.publishedDraftGeneration(),
    };
    this.beginPublishedDraft(target);
  }
  onCatalogDraftRequested(row: WorkflowDefinitionLifecycleDetail): void {
    const workspace = this.draftWorkspace;
    if (
      !workspace ||
      !this.canCreateWorkflow() ||
      !this.isVersionCatalogVisible() ||
      this.catalogQuery().workflowCode !== row.workflowCode ||
      this.isLoadingList() ||
      this.catalogError() ||
      this.isLoadingDetail() ||
      workspace.isDraftEditorInteractionBlocked() ||
      !workflowPublishedDraftAction(row.draftActionCode) ||
      !this.publishedDraftNativeCurrent() ||
      !this.definitions().some((current) => current === row)
    )
      return;
    const action = workflowPublishedDraftAction(row.draftActionCode);
    if (!action) return;
    this.beginPublishedDraft({
      kind: 'catalog',
      source: row,
      action,
      context: workflowPublishedDraftSourceContext(row),
      actionContext: this.publishedDraftActionContext(row),
      actor: this.publishedDraftActor(),
      generation: this.publishedDraftGeneration(),
    });
  }
  retryPublishedDraft(failure: PublishedDraftFailure): void {
    if (
      this.publishedDraftFailure() !== failure ||
      !this.publishedDraftNativeCurrent()
    )
      return;
    this.beginPublishedDraft({ ...failure.target });
  }
  publishedDraftFailureMessage(failure: PublishedDraftFailure): string {
    const error = failure.error;
    const fallback =
      'Workflow registry diagnostics could not be loaded. Try again or contact support if the problem persists.';
    const genericTitle =
      error.title === 'Workflow registry diagnostics unavailable' ||
      error.title === 'Unexpected error';
    const title = genericTitle
      ? 'Draft opening could not be confirmed'
      : error.title;
    const messages = error.messages.filter((message) => message !== fallback);
    return [
      /[.!?]$/.test(title) ? title : `${title}.`,
      ...messages,
      ...(genericTitle || messages.length !== error.messages.length
        ? [
            'This attempt may already have opened a draft. Retry opening from this same published source.',
          ]
        : []),
      ...(error.correlationId
        ? [`Support reference: ${error.correlationId}`]
        : []),
    ].join(' ');
  }
  private beginPublishedDraft(target: PublishedDraftTarget): void {
    const workspace = this.draftWorkspace;
    if (
      !workspace ||
      workspace.isDraftEditorInteractionBlocked() ||
      !this.publishedDraftCurrent(target)
    )
      return;
    this.publishedDraftTarget.set(target);
    this.publishedDraftError.set(null);
    if (target.action === 'ambiguous') {
      workspace.openWorkflowDrafts(
        target.source.workflowCode,
        target.source.id,
        false,
        {
          isCurrent: () =>
            this.publishedDraftTarget() === target &&
            this.publishedDraftCurrent(target),
          onOpened: () => {
            if (this.publishedDraftTarget() !== target) return;
            this.publishedDraftTarget.set(null);
            this.publishedDraftError.set(null);
            this.registryView.set('drafts');
            this.writeRegistryViewToUrl('drafts');
          },
        },
      );
      return;
    }
    const handoff: WorkflowPublishedDraftHandoff = {
      sourceDefinitionId: target.source.id,
      sourceContext:
        target.kind === 'version'
          ? target.request.sourceContext
          : target.context,
      isCurrent: () =>
        this.publishedDraftTarget() === target &&
        this.publishedDraftCurrent(target),
      onFailed: (error) => {
        if (
          this.publishedDraftTarget() === target &&
          this.publishedDraftCurrent(target)
        )
          this.publishedDraftError.set({
            target,
            error: structuredClone(error),
          });
      },
    };
    workspace.editPublishedVersion(target.source, handoff);
  }
  private publishedDraftActor(): string {
    return JSON.stringify([
      this.roles.accessProfile?.()?.actor ?? null,
      this.canCreateWorkflow(),
    ]);
  }
  private publishedDraftActionContext(source: {
    draftActionCode?: unknown;
    editableDraftId?: number | null;
  }): string {
    return JSON.stringify([
      source.draftActionCode,
      Object.hasOwn(source, 'editableDraftId'),
      source.editableDraftId,
    ]);
  }
  private publishedDraftNativeCurrent(): boolean {
    return (
      this.publishedDraftHostCurrent() &&
      !this.element.nativeElement.closest('[aria-hidden="true"]')
    );
  }
  private publishedDraftHostCurrent(): boolean {
    const host = this.element.nativeElement;
    if (
      this.destroyRef.destroyed ||
      this.registryAccessDenied() ||
      !this.canCreateWorkflow() ||
      !host.isConnected ||
      host.closest('[hidden],[inert]')
    )
      return false;
    for (let node: HTMLElement | null = host; node; node = node.parentElement) {
      const style = host.ownerDocument.defaultView?.getComputedStyle(node);
      if (
        style?.display === 'none' ||
        style?.visibility === 'hidden' ||
        style?.visibility === 'collapse'
      )
        return false;
    }
    return true;
  }
  private publishedDraftCurrent(target: PublishedDraftTarget): boolean {
    if (
      !this.publishedDraftHostCurrent() ||
      target.generation !== this.publishedDraftGeneration() ||
      target.actor !== this.publishedDraftActor() ||
      target.context !== workflowPublishedDraftSourceContext(target.source) ||
      target.action !==
        workflowPublishedDraftAction(target.source.draftActionCode) ||
      target.actionContext !==
        this.publishedDraftActionContext(target.source) ||
      this.isLoadingDetail()
    )
      return false;
    if (target.kind === 'catalog')
      return (
        this.isVersionCatalogVisible() &&
        !this.isLoadingList() &&
        !this.catalogError() &&
        this.catalogQuery().workflowCode === target.source.workflowCode &&
        this.definitions().includes(target.source)
      );
    return (
      this.isVersionDetailVisible() &&
      this.versionWorkspace === target.editor &&
      this.selectedDefinition() === target.source &&
      target.source.definitionDocumentStatusCode === 'readable' &&
      target.request.view === this.activeVersionView() &&
      target.request.ownerContext === target.editor.availabilityOwnerContext &&
      this.selectedDefinitionKey()?.workflowCode ===
        target.source.workflowCode &&
      this.selectedDefinitionKey()?.definitionVersionCode ===
        target.source.definitionVersionCode &&
      target.editor.isDraftEditTargetCurrent(target.request, target.source)
    );
  }

  openPublishedSource(selection: WorkflowDefinitionSelection): void {
    this.requestDefinitionSelection(selection, true, undefined, 'version');
  }

  onDefinitionPublished(selection: WorkflowDefinitionSelection): void {
    this.searchDefinitions(this.catalogQuery());
    this.requestDefinitionSelection(
      selection,
      true,
      undefined,
      'version',
      true,
    );
  }

  onDefinitionLifecycleChanged(): void {
    this.newlyPublishedVersion.set(null);
    this.searchDefinitions(this.catalogQuery());
    this.retrySelectedDefinition();
  }

  private requestDefinitionSelection(
    selection: WorkflowDefinitionSelection,
    updateUrl: boolean,
    onCancel?: () => void,
    targetView: WorkflowRegistryView = 'version',
    publicationHandoff = false,
  ): void {
    const applySelection = () => {
      this.publishedDraftGeneration.update((generation) => generation + 1);
      const isCurrent = this.isSelectionCurrent(selection);

      this.selectedDefinitionKey.set(selection);
      this.registryView.set(targetView);
      this.newlyPublishedVersion.set(publicationHandoff ? selection : null);
      this.shouldFocusActivationAfterLoad =
        targetView === 'version' && publicationHandoff;
      this.shouldFocusVersionBackAfterLoad =
        targetView === 'version' && updateUrl && !publicationHandoff;
      if (targetView === 'version' && updateUrl) {
        this.activeVersionView.set(publicationHandoff ? 'review' : 'map');
      }
      if (updateUrl && targetView === 'version' && !publicationHandoff) {
        setTimeout(() => this.focusVersionBackAction());
      }
      if (updateUrl) {
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            workflow: selection.workflowCode,
            version: selection.definitionVersionCode,
            registryView: targetView,
            draft: null,
            draftView: null,
            routingView: null,
            reviewView: null,
            versionView: publicationHandoff ? 'review' : null,
          },
          queryParamsHandling: 'merge',
          preserveFragment: true,
          replaceUrl: true,
        });
      } else if (
        targetView !== 'drafts' &&
        readPositiveInteger(this.route.snapshot.queryParamMap.get('draft')) !==
          null
      ) {
        this.writeRegistryViewToUrl(targetView);
      }

      if (isCurrent && this.detailResult()) {
        if (publicationHandoff) {
          this.shouldFocusActivationAfterLoad = false;
          setTimeout(() => this.focusPublishedVersionActivation(selection));
        }
        return;
      }

      this.loadDefinition(
        selection.workflowCode,
        selection.definitionVersionCode,
      );
    };

    if (this.draftWorkspace) {
      this.draftWorkspace.requestExternalSelectionChange(
        applySelection,
        onCancel,
      );
      return;
    }

    applySelection();
  }

  retrySelectedDefinition(): void {
    const selection = this.selectedDefinitionKey();
    if (selection) {
      this.loadDefinition(
        selection.workflowCode,
        selection.definitionVersionCode,
      );
    }
  }

  formatMessage(
    result: WorkflowRegistryDiagnosticResult<unknown> | null,
  ): string {
    if (!result) {
      return '';
    }

    if (result.state === 'resolved') {
      return 'Resolved';
    }

    return (
      result.error?.messages.join(' ') ??
      'Workflow registry diagnostics unavailable.'
    );
  }

  formatRequiredOutcome(
    decision: WorkflowDecisionRequirementDiagnostic,
  ): string {
    return normalizeOptionalText(decision.requiredOutcomeCode) ?? 'approved';
  }

  formatTargetState(decision: WorkflowDecisionRequirementDiagnostic): string {
    return normalizeOptionalText(decision.targetStateCode) ?? 'none';
  }

  formatCodeLabel(value: string | null | undefined): string {
    const normalized = normalizeOptionalText(value);
    if (!normalized) {
      return 'Unassigned';
    }

    return normalized
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  formatDecisionOwner(
    decision: WorkflowDecisionRequirementDiagnostic | null | undefined,
  ): string {
    if (!decision) {
      return 'Unassigned';
    }

    return this.formatCodeLabel(decision.ownerQueueCode);
  }

  formatDecisionRoles(
    decision: WorkflowDecisionRequirementDiagnostic | null | undefined,
  ): string {
    const roles = decision?.eligibleRoleCodes ?? [];
    return roles.length > 0
      ? roles.map((role) => this.formatCodeLabel(role)).join(', ')
      : 'Not specified';
  }

  formatDecisionRequirementLabel(
    decision: WorkflowDecisionRequirementDiagnostic,
  ): string {
    const outcome = this.formatRequiredOutcome(decision);
    const target = normalizeOptionalText(decision.targetStateCode);
    return target
      ? `${decision.decisionKind}: ${outcome} -> ${target}`
      : `${decision.decisionKind}: ${outcome}`;
  }

  formatTransitionDecisionSummary(
    transition: WorkflowTransitionDiagnostic | null | undefined,
  ): string {
    if (!transition || transition.requiredDecisions.length === 0) {
      return 'No decision gate';
    }

    return transition.requiredDecisions
      .map((decision) => this.formatDecisionRequirementLabel(decision))
      .join(', ');
  }

  formatTransitionDecisionCount(
    transition: WorkflowTransitionDiagnostic | null | undefined,
  ): number {
    return transition?.requiredDecisions.length ?? 0;
  }

  formatStepDecisionCount(
    step: WorkflowStepDefinitionDiagnostic | null | undefined,
  ): number {
    if (!step) {
      return 0;
    }

    return (
      step.requiredDecisions.length +
      step.allowedTransitions.reduce(
        (total, transition) => total + transition.requiredDecisions.length,
        0,
      )
    );
  }

  getStepGatedTransitions(
    step: WorkflowStepDefinitionDiagnostic | null | undefined,
  ): WorkflowTransitionDiagnostic[] {
    return (
      step?.allowedTransitions.filter(
        (transition) => transition.requiredDecisions.length > 0,
      ) ?? []
    );
  }

  getStepUngatedTransitionCount(
    step: WorkflowStepDefinitionDiagnostic | null | undefined,
  ): number {
    return (
      step?.allowedTransitions.filter(
        (transition) => transition.requiredDecisions.length === 0,
      ).length ?? 0
    );
  }

  formatTransitionLabel(
    transition: WorkflowTransitionDiagnostic | null | undefined,
  ): string {
    if (!transition) {
      return '';
    }

    return `${transition.fromStateCode} -> ${transition.toStateCode}`;
  }

  formatStepTransitions(
    step: WorkflowStepDefinitionDiagnostic | null | undefined,
  ): string {
    if (!step || step.allowedTransitions.length === 0) {
      return 'none';
    }

    return step.allowedTransitions
      .map(
        (transition) =>
          `${transition.fromStateCode} to ${transition.toStateCode}`,
      )
      .join(', ');
  }

  getStepDecisionRequirementRows(
    step: WorkflowStepDefinitionDiagnostic | null | undefined,
  ): WorkflowDecisionRequirementRow[] {
    if (!step) {
      return [];
    }

    const rows: WorkflowDecisionRequirementRow[] = [];

    step.requiredDecisions.forEach((decision) => {
      rows.push({
        ...decision,
        sourceType: 'Step',
        sourceLabel: step.stepCode,
      });
    });

    step.allowedTransitions.forEach((transition) => {
      transition.requiredDecisions.forEach((decision) => {
        rows.push({
          ...decision,
          sourceType: 'Step Transition',
          sourceLabel: this.formatTransitionLabel(transition),
        });
      });
    });

    return rows;
  }

  formatAlertOutcome(
    evaluation: WorkflowAlertEvaluation | null | undefined,
  ): string {
    return evaluation?.outcomeCode ?? '';
  }

  formatAlertRecipient(
    row: WorkflowAlertRule | WorkflowAlertEvaluation | null | undefined,
  ): string {
    if (!row) {
      return '';
    }

    switch (row.recipientTargetTypeCode) {
      case 'user':
        return row.recipientUserId ? `User ${row.recipientUserId}` : 'User';
      case 'role':
        return row.recipientTargetKey
          ? `Role ${row.recipientTargetKey}`
          : 'Role';
      case 'queue':
        return row.recipientTargetKey
          ? `Queue ${row.recipientTargetKey}`
          : 'Current Queue';
      case 'current_assignee':
        return row.recipientUserId
          ? `Assignee ${row.recipientUserId}`
          : 'Current Assignee';
      default:
        return row.recipientTargetTypeCode ?? '';
    }
  }

  formatEffectiveWindow(
    definition: WorkflowDefinitionVersionSummary | null | undefined,
  ): string {
    if (!definition) {
      return '';
    }

    const start = formatDateTimeLabel(definition.effectiveStartUtc);
    const end = formatDateTimeLabel(definition.effectiveEndUtc);
    return end ? `${start} to ${end}` : `${start} onward`;
  }

  selectAlertRule(rule: WorkflowAlertRule): void {
    if (!this.canManageAlertRules()) {
      return;
    }

    this.alertRuleFormValues = {
      ruleCode: rule.ruleCode,
      displayName: rule.displayName,
      eventTypeCode: rule.eventTypeCode,
      workflowCode: rule.workflowCode ?? '',
      recipientTargetTypeCode: rule.recipientTargetTypeCode,
      recipientUserId: rule.recipientUserId ?? null,
      recipientTargetKey: rule.recipientTargetKey ?? '',
      alertTitle: rule.alertTitle,
      alertMessageTemplate: rule.alertMessageTemplate,
      severityCode: rule.severityCode,
      isActive: rule.isActive,
    };
  }

  saveAlertRule(form = this.alertRuleForm): void {
    if (!this.canManageAlertRules()) {
      return;
    }

    if (!this.ensureFormIsValid(form)) {
      return;
    }

    this.isSavingAlertRule.set(true);
    const request = this.mapAlertRuleRequest();
    this.diagnosticsService
      .upsertAlertRule(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.isSavingAlertRule.set(false);
        if (result.state === 'resolved') {
          this.loadAlertRules();
        } else {
          this.alertRulesResult.set({
            state: result.state,
            data: [],
            error: result.error,
          });
        }
      });
  }

  previewAlertRules(form = this.alertPreviewForm): void {
    if (!this.canReadWorkflowRuntime()) {
      return;
    }

    if (!this.ensureFormIsValid(form)) {
      return;
    }

    this.isPreviewingAlertRules.set(true);
    this.alertPreviewResult.set(null);
    this.alertPreviewSubscription?.unsubscribe();
    const requestId = ++this.alertPreviewRequestId;

    this.alertPreviewSubscription = this.diagnosticsService
      .previewAlertRules({
        eventTypeCode: this.alertPreviewFormValues.eventTypeCode.trim(),
        workflowInstanceId: toOptionalNumber(
          this.alertPreviewFormValues.workflowInstanceId,
        ),
        correlationKey:
          normalizeOptionalText(this.alertPreviewFormValues.correlationKey) ??
          null,
        workflowCode:
          normalizeOptionalText(this.alertPreviewFormValues.workflowCode) ??
          null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (requestId !== this.alertPreviewRequestId) {
          return;
        }

        this.alertPreviewResult.set(result);
        this.isPreviewingAlertRules.set(false);
      });
  }

  private loadDefinition(
    workflowCode: string,
    definitionVersionCode: string,
  ): void {
    this.isLoadingDetail.set(true);
    this.detailResult.set(null);
    this.detailLoadSubscription?.unsubscribe();
    const requestId = ++this.detailLoadRequestId;
    this.resetDefinitionDependencies();

    this.detailLoadSubscription = this.diagnosticsService
      .getDefinition(workflowCode.trim(), definitionVersionCode.trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (requestId !== this.detailLoadRequestId) {
          return;
        }

        this.detailResult.set(result);
        this.isLoadingDetail.set(false);
        if (result.state === 'forbidden' || result.error?.statusCode === 403) {
          this.invalidateVisibilityReads();
          this.draftWorkspace?.suspendRegistryReadAccess(
            result.error?.statusCode === 401 ? 401 : 403,
          );
          this.catalogLoadSubscription?.unsubscribe();
          this.catalogLoadRequestId++;
          this.isLoadingList.set(false);
          this.registryAccessDenied.set(true);
          this.catalogPage.set(null);
          this.localExample.set(null);
        }
        if (result.state === 'resolved' && result.data) {
          this.registryAccessDenied.set(false);
          this.refreshFamilyVisibility(workflowCode, false);
          this.draftWorkspace?.resumeConfirmedRegistryReadAccess();
          this.alertRuleFormValues = {
            ...this.alertRuleFormValues,
            workflowCode: result.data.workflowCode,
          };
          this.alertPreviewFormValues = {
            ...this.alertPreviewFormValues,
            workflowCode: result.data.workflowCode,
          };
          this.loadAlertRules(result.data.workflowCode);
          this.loadDecisionRecords(result.data);
          if (
            this.shouldFocusVersionBackAfterLoad &&
            this.registryView() === 'version'
          ) {
            this.shouldFocusVersionBackAfterLoad = false;
            setTimeout(() => this.focusVersionBackAction());
          }
          if (
            this.shouldFocusActivationAfterLoad &&
            this.registryView() === 'version'
          ) {
            this.shouldFocusActivationAfterLoad = false;
            const selection = { workflowCode, definitionVersionCode };
            setTimeout(() => this.focusPublishedVersionActivation(selection));
          }
        }
      });
  }

  private focusVersionBackAction(): void {
    if (this.versionWorkspace) {
      this.versionWorkspace.focusBackAction();
      return;
    }

    this.detailLoadingBackButton?.nativeElement.focus();
  }

  private focusPublishedVersionActivation(
    selection: WorkflowDefinitionSelection,
  ): void {
    if (
      this.isSelectionCurrent(selection) &&
      this.registryView() === 'version' &&
      this.activeVersionView() === 'review'
    ) {
      this.versionWorkspace?.reviewAvailabilityControls();
    }
  }

  private isSelectionCurrent(
    selection: WorkflowDefinitionSelection | null,
  ): boolean {
    if (!selection) {
      return false;
    }

    const current = this.selectedDefinitionKey();
    return (
      current?.workflowCode === selection.workflowCode &&
      current.definitionVersionCode === selection.definitionVersionCode
    );
  }

  private writeCatalogQueryToUrl(query: WorkflowDefinitionCatalogQuery): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        catalogSearch: query.search ?? null,
        catalogWorkflow: query.workflowCode ?? null,
        catalogLifecycle: query.lifecycleStatusCode ?? null,
        catalogActivation: query.activationPostureCode ?? null,
        catalogIncludeHidden: query.includeHidden === 'true' ? 'true' : null,
        catalogPage: query.pageNumber > 1 ? query.pageNumber : null,
        catalogPageSize: query.pageSize !== 25 ? query.pageSize : null,
      },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  private writeRegistryViewToUrl(view: WorkflowRegistryView): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams:
        view === 'drafts'
          ? { ...this.draftWorkspace?.draftDiscoveryRouteParams(), registryView: view, versionView: null }
          : {
              registryView: view,
              draft: null,
              draftView: null,
              routingView: null,
              reviewView: null,
              versionView: null,
            },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  private restoreExternalRegistryContextUrl(): void {
    const workspace = this.draftWorkspace;
    const draft = workspace?.currentDraft() ?? null;
    const selection = this.selectedDefinitionKey();
    const draftView = draft
      ? (workspace?.activeWorkbenchView() ?? 'map')
      : 'map';
    const routingView = draft
      ? (workspace?.activeRoutingView() ?? 'paths')
      : 'paths';
    const reviewView =
      draftView === 'review'
        ? (workspace?.activeReviewView() ?? 'readiness')
        : 'readiness';
    const registryView: WorkflowRegistryView =
      draft || workspace?.authoringMode() ? 'drafts' : this.registryView();

    this.registryView.set(registryView);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        registryView,
        workflow: selection?.workflowCode ?? null,
        version: selection?.definitionVersionCode ?? null,
        draft: draft?.id ?? null,
        draftView: draftView === 'map' ? null : draftView,
        routingView: routingView === 'paths' ? null : routingView,
        reviewView:
          draftView === 'review' && reviewView !== 'readiness'
            ? reviewView
            : null,
        ...(draft ? workspace?.designerContextQuery() : {}),
        versionView:
          registryView === 'version' && this.activeVersionView() !== 'map'
            ? this.activeVersionView()
            : null,
      },
      queryParamsHandling: 'merge',
      preserveFragment: true,
      replaceUrl: true,
    });
  }

  private focusAfterRender(
    target: () => ElementRef<HTMLElement> | undefined,
  ): void {
    setTimeout(() => target()?.nativeElement.focus());
  }

  loadAlertRules(
    workflowCode = this.selectedDefinition()?.workflowCode ??
      this.alertRuleFormValues.workflowCode,
  ): void {
    if (!this.canReadWorkflowRuntime()) {
      this.alertRulesResult.set(null);
      this.isLoadingAlertRules.set(false);
      return;
    }

    this.isLoadingAlertRules.set(true);
    this.alertRulesLoadSubscription?.unsubscribe();
    const requestId = ++this.alertRulesLoadRequestId;

    this.alertRulesLoadSubscription = this.diagnosticsService
      .listAlertRules({
        workflowCode: normalizeOptionalText(workflowCode),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (requestId !== this.alertRulesLoadRequestId) {
          return;
        }

        this.alertRulesResult.set(result);
        this.isLoadingAlertRules.set(false);
      });
  }

  private loadDecisionRecords(
    definition: WorkflowDefinitionVersionDetail,
  ): void {
    if (!this.canReadWorkflowRuntime()) {
      this.decisionRecordsResult.set(null);
      this.isLoadingDecisionRecords.set(false);
      return;
    }

    this.isLoadingDecisionRecords.set(true);
    this.decisionRecordsResult.set(null);
    this.decisionRecordsLoadSubscription?.unsubscribe();
    const requestId = ++this.decisionRecordsLoadRequestId;

    this.decisionRecordsLoadSubscription = this.diagnosticsService
      .listDecisionRecords({
        subjectTypeCode: definition.subjectTypeCode ?? undefined,
        pageNumber: 1,
        pageSize: 50,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (requestId !== this.decisionRecordsLoadRequestId) {
          return;
        }

        this.decisionRecordsResult.set(result);
        this.isLoadingDecisionRecords.set(false);
      });
  }

  private resetDefinitionDependencies(): void {
    this.alertRulesLoadSubscription?.unsubscribe();
    this.alertRulesLoadRequestId++;
    this.alertRulesResult.set(null);
    this.isLoadingAlertRules.set(false);

    this.alertPreviewSubscription?.unsubscribe();
    this.alertPreviewRequestId++;
    this.alertPreviewResult.set(null);
    this.isPreviewingAlertRules.set(false);

    this.decisionRecordsLoadSubscription?.unsubscribe();
    this.decisionRecordsLoadRequestId++;
    this.decisionRecordsResult.set(null);
    this.isLoadingDecisionRecords.set(false);
  }

  private ensureFormIsValid(form: EzFormComponent | undefined): boolean {
    const formGroup = form?.theFormGroup;
    if (!formGroup || formGroup.valid) {
      return true;
    }

    formGroup.markAllAsTouched();
    return false;
  }

  private buildAlertRuleFormConfig(): InputConfig[] {
    return [
      {
        name: 'ruleCode',
        type: EzFormInputType.TEXT,
        label: 'Rule Code',
        row: 1,
        width: '1x',
        validation: [Validators.required],
      },
      {
        name: 'displayName',
        type: EzFormInputType.TEXT,
        label: 'Name',
        row: 1,
        width: '1x',
        validation: [Validators.required],
      },
      {
        name: 'eventTypeCode',
        type: EzFormInputType.TEXT,
        label: 'When Event',
        row: 2,
        width: '1x',
        validation: [Validators.required],
      },
      {
        name: 'workflowCode',
        type: EzFormInputType.TEXT,
        label: 'Workflow',
        row: 2,
        width: '1x',
      },
      {
        name: 'recipientTargetTypeCode',
        type: EzFormInputType.SELECT,
        label: 'Recipient Type',
        row: 3,
        width: '1x',
        options: RECIPIENT_TYPE_OPTIONS,
        validation: [Validators.required],
      },
      {
        name: 'recipientUserId',
        type: EzFormInputType.NUMBER,
        label: 'Recipient User',
        row: 3,
        width: '1x',
      },
      {
        name: 'recipientTargetKey',
        type: EzFormInputType.TEXT,
        label: 'Role / Queue Code',
        row: 3,
        width: '1x',
      },
      {
        name: 'severityCode',
        type: EzFormInputType.SELECT,
        label: 'Severity',
        row: 4,
        width: '1x',
        options: SEVERITY_OPTIONS,
        validation: [Validators.required],
      },
      {
        name: 'isActive',
        type: EzFormInputType.CHECKBOX,
        label: 'Active',
        row: 4,
        width: '1x',
      },
      {
        name: 'alertTitle',
        type: EzFormInputType.TEXT,
        label: 'Title',
        row: 5,
        width: 'fill',
        validation: [Validators.required],
      },
      {
        name: 'alertMessageTemplate',
        type: EzFormInputType.TEXTAREA,
        label: 'Message',
        row: 6,
        width: 'fill',
        validation: [Validators.required],
      },
    ];
  }

  private buildAlertPreviewFormConfig(): InputConfig[] {
    return [
      {
        name: 'eventTypeCode',
        type: EzFormInputType.TEXT,
        label: 'Event',
        row: 1,
        width: '1x',
        validation: [Validators.required],
      },
      {
        name: 'workflowInstanceId',
        type: EzFormInputType.NUMBER,
        label: 'Workflow Instance',
        row: 1,
        width: '1x',
      },
      {
        name: 'correlationKey',
        type: EzFormInputType.TEXT,
        label: 'Correlation Key',
        row: 2,
        width: '1x',
      },
      {
        name: 'workflowCode',
        type: EzFormInputType.TEXT,
        label: 'Workflow',
        row: 2,
        width: '1x',
      },
    ];
  }

  private buildDefinitionColumnDefs(): ColDef[] {
    return [
      { field: 'workflowCode', headerName: 'Workflow', flex: 1, minWidth: 180 },
      {
        field: 'definitionVersionCode',
        headerName: 'Version',
        flex: 1,
        minWidth: 150,
      },
      { field: 'lifecycleStatusCode', headerName: 'Lifecycle', width: 130 },
      { field: 'activationPostureCode', headerName: 'Activation', width: 130 },
      { field: 'validationStatusCode', headerName: 'Validation', width: 125 },
      { field: 'runtimeBindingCode', headerName: 'Binding', width: 140 },
      {
        headerName: '',
        width: 90,
        maxWidth: 90,
        sortable: false,
        cellRenderer: ButtonCellRenderer,
        cellRendererParams: {
          label: 'Select',
          onClick: (params: { rowData: WorkflowDefinitionLifecycleDetail }) =>
            this.selectDefinition(params.rowData),
        },
      },
    ];
  }

  private buildWorkflowTransitionColumnDefs(): ColDef[] {
    return [
      { field: 'fromStateCode', headerName: 'From', flex: 1, minWidth: 150 },
      { field: 'toStateCode', headerName: 'To', flex: 1, minWidth: 150 },
      {
        headerName: 'Decisions',
        width: 130,
        valueGetter: ({ data }) =>
          this.formatTransitionDecisionCount(
            data as WorkflowTransitionDiagnostic,
          ),
      },
    ];
  }

  private buildAlertRuleColumnDefs(): ColDef[] {
    return [
      { field: 'ruleCode', headerName: 'Rule', flex: 1, minWidth: 180 },
      { field: 'eventTypeCode', headerName: 'Event', flex: 1, minWidth: 190 },
      {
        headerName: 'Recipient',
        flex: 1,
        minWidth: 170,
        valueGetter: ({ data }) =>
          this.formatAlertRecipient(data as WorkflowAlertRule),
      },
      { field: 'severityCode', headerName: 'Severity', width: 110 },
      { field: 'isActive', headerName: 'Active', width: 100 },
      {
        headerName: '',
        width: 90,
        maxWidth: 90,
        sortable: false,
        cellRenderer: ButtonCellRenderer,
        cellRendererParams: {
          label: 'Edit',
          onClick: (params: { rowData: WorkflowAlertRule }) =>
            this.selectAlertRule(params.rowData),
        },
      },
    ];
  }

  private buildAlertEvaluationColumnDefs(): ColDef[] {
    return [
      { field: 'ruleCode', headerName: 'Rule', flex: 1, minWidth: 180 },
      {
        headerName: 'Outcome',
        flex: 1,
        minWidth: 170,
        valueGetter: ({ data }) =>
          this.formatAlertOutcome(data as WorkflowAlertEvaluation),
      },
      {
        headerName: 'Recipient',
        flex: 1,
        minWidth: 170,
        valueGetter: ({ data }) =>
          this.formatAlertRecipient(data as WorkflowAlertEvaluation),
      },
      { field: 'reasonCode', headerName: 'Reason', flex: 1, minWidth: 180 },
    ];
  }

  private buildStepColumnDefs(): ColDef[] {
    return [
      { field: 'stepCode', headerName: 'Step', flex: 1, minWidth: 170 },
      {
        headerName: 'Transitions',
        flex: 2,
        minWidth: 260,
        valueGetter: ({ data }) =>
          this.formatStepTransitions(data as WorkflowStepDefinitionDiagnostic),
      },
      {
        headerName: 'Required Decisions',
        width: 170,
        valueGetter: ({ data }) =>
          this.formatStepDecisionCount(
            data as WorkflowStepDefinitionDiagnostic,
          ),
      },
    ];
  }

  private buildDecisionRequirementColumnDefs(): ColDef[] {
    return [
      { field: 'sourceType', headerName: 'Source', width: 170 },
      { field: 'sourceLabel', headerName: 'Rule', flex: 1.4, minWidth: 220 },
      { field: 'decisionKind', headerName: 'Kind', flex: 1, minWidth: 140 },
      {
        headerName: 'Outcome',
        flex: 1,
        minWidth: 130,
        valueGetter: ({ data }) =>
          this.formatRequiredOutcome(
            data as WorkflowDecisionRequirementDiagnostic,
          ),
      },
      { field: 'reactionCode', headerName: 'Reaction', flex: 1, minWidth: 130 },
      {
        headerName: 'Target',
        flex: 1,
        minWidth: 130,
        valueGetter: ({ data }) =>
          this.formatTargetState(data as WorkflowDecisionRequirementDiagnostic),
      },
      {
        headerName: 'Eligible Roles',
        flex: 1.2,
        minWidth: 180,
        valueGetter: ({ data }) =>
          this.formatDecisionRoles(
            data as WorkflowDecisionRequirementDiagnostic,
          ),
      },
      {
        headerName: 'Owner Queue',
        flex: 1,
        minWidth: 150,
        valueGetter: ({ data }) =>
          this.formatDecisionOwner(
            data as WorkflowDecisionRequirementDiagnostic,
          ),
      },
    ];
  }

  private buildDecisionRecordColumnDefs(): ColDef[] {
    return [
      { field: 'id', headerName: 'ID', width: 90 },
      { field: 'decisionRecordTypeCode', headerName: 'Type', width: 140 },
      { field: 'statusCode', headerName: 'Status', width: 130 },
      { field: 'subjectId', headerName: 'Subject', width: 120 },
      {
        headerName: 'Workflow',
        width: 130,
        valueGetter: ({ data }) =>
          (data as WorkflowDecisionRecord | undefined)?.workflowInstanceId ??
          '',
      },
      {
        field: 'decisionReasonCode',
        headerName: 'Reason',
        flex: 1,
        minWidth: 200,
      },
      {
        headerName: 'Changed',
        flex: 1,
        minWidth: 170,
        valueGetter: ({ data }) =>
          formatDateTimeLabel(
            (data as WorkflowDecisionRecord | undefined)?.statusChangedUtc,
          ),
      },
      {
        headerName: 'Due',
        flex: 1,
        minWidth: 160,
        valueGetter: ({ data }) =>
          formatDateTimeLabel(
            (data as WorkflowDecisionRecord | undefined)?.dueUtc,
          ),
      },
    ];
  }

  private buildDecisionColumnDefs(): ColDef[] {
    return [
      { field: 'decisionKind', headerName: 'Kind', flex: 1, minWidth: 160 },
      {
        headerName: 'Outcome',
        flex: 1,
        minWidth: 140,
        valueGetter: ({ data }) =>
          this.formatRequiredOutcome(
            data as WorkflowDecisionRequirementDiagnostic,
          ),
      },
      { field: 'reactionCode', headerName: 'Reaction', flex: 1, minWidth: 140 },
      {
        headerName: 'Target',
        flex: 1,
        minWidth: 140,
        valueGetter: ({ data }) =>
          this.formatTargetState(data as WorkflowDecisionRequirementDiagnostic),
      },
    ];
  }

  private mapAlertRuleRequest(): WorkflowAlertRuleUpsertRequest {
    return {
      ruleCode: this.alertRuleFormValues.ruleCode.trim(),
      displayName: this.alertRuleFormValues.displayName.trim(),
      eventTypeCode: this.alertRuleFormValues.eventTypeCode.trim(),
      workflowCode:
        normalizeOptionalText(this.alertRuleFormValues.workflowCode) ?? null,
      recipientTargetTypeCode:
        this.alertRuleFormValues.recipientTargetTypeCode.trim(),
      recipientUserId: toOptionalNumber(
        this.alertRuleFormValues.recipientUserId,
      ),
      recipientTargetKey:
        normalizeOptionalText(this.alertRuleFormValues.recipientTargetKey) ??
        null,
      alertTitle: this.alertRuleFormValues.alertTitle.trim(),
      alertMessageTemplate:
        this.alertRuleFormValues.alertMessageTemplate.trim(),
      severityCode: this.alertRuleFormValues.severityCode.trim(),
      isActive: Boolean(this.alertRuleFormValues.isActive),
    };
  }

  private buildDecisionRequirementRows(): WorkflowDecisionRequirementRow[] {
    const rows: WorkflowDecisionRequirementRow[] = [];

    this.requiredDecisions().forEach((decision) => {
      rows.push({
        ...decision,
        sourceType: 'Definition',
        sourceLabel: 'Definition-level gate',
      });
    });

    this.workflowTransitions().forEach((transition) => {
      transition.requiredDecisions.forEach((decision) => {
        rows.push({
          ...decision,
          sourceType: 'Workflow Transition',
          sourceLabel: this.formatTransitionLabel(transition),
        });
      });
    });

    this.stepDefinitions().forEach((step) => {
      step.requiredDecisions.forEach((decision) => {
        rows.push({
          ...decision,
          sourceType: 'Step',
          sourceLabel: step.stepCode,
        });
      });

      step.allowedTransitions.forEach((transition) => {
        transition.requiredDecisions.forEach((decision) => {
          rows.push({
            ...decision,
            sourceType: 'Step Transition',
            sourceLabel: `${step.stepCode}: ${this.formatTransitionLabel(transition)}`,
          });
        });
      });
    });

    return rows;
  }
}

function normalizeOptionalText(
  value: string | null | undefined,
): string | undefined {
  const normalized = value?.trim() ?? '';
  return normalized.length > 0 ? normalized : undefined;
}

function readWorkflowSelection(
  workflowCode: string | null | undefined,
  definitionVersionCode: string | null | undefined,
): WorkflowDefinitionSelection | null {
  const normalizedWorkflowCode = normalizeOptionalText(workflowCode);
  const normalizedVersionCode = normalizeOptionalText(definitionVersionCode);
  if (!normalizedWorkflowCode || !normalizedVersionCode) {
    return null;
  }

  return {
    workflowCode: normalizedWorkflowCode,
    definitionVersionCode: normalizedVersionCode,
  };
}

function readRegistryView(
  queryParams: ParamMap,
  selection: WorkflowDefinitionSelection | null,
): WorkflowRegistryView {
  const requestedView = queryParams.get('registryView')?.trim().toLowerCase();
  if (requestedView === 'drafts') {
    return 'drafts';
  }
  if (requestedView === 'versions') {
    return 'versions';
  }
  if (requestedView === 'version' && selection) {
    return 'version';
  }

  // Draft and immutable-version deep links predate registryView. Preserve
  // those entry points while allowing an explicit browse view to retain a
  // selected version as the source for a later clone.
  if (readPositiveInteger(queryParams.get('draft')) !== null) {
    return 'drafts';
  }
  return selection ? 'version' : 'versions';
}

function readVersionWorkbenchView(
  queryParams: ParamMap,
): WorkflowVersionWorkbenchView {
  const requestedView = queryParams.get('versionView')?.trim().toLowerCase();
  return requestedView === 'setup' || requestedView === 'review'
    ? requestedView
    : 'map';
}

function readCatalogQuery(
  queryParams: ParamMap,
): WorkflowDefinitionCatalogQuery {
  return normalizeCatalogQuery({
    search: queryParams.get('catalogSearch') ?? undefined,
    workflowCode: queryParams.get('catalogWorkflow') ?? undefined,
    lifecycleStatusCode: queryParams.get('catalogLifecycle') ?? undefined,
    activationPostureCode: queryParams.get('catalogActivation') ?? undefined,
    includeHidden: queryParams.get('catalogIncludeHidden') === 'true' ? 'true' : undefined,
    pageNumber: toPositiveInteger(queryParams.get('catalogPage'), 1),
    pageSize: toPositiveInteger(queryParams.get('catalogPageSize'), 25),
  });
}

function catalogQueriesEqual(
  left: WorkflowDefinitionCatalogQuery,
  right: WorkflowDefinitionCatalogQuery,
): boolean {
  return (
    left.search === right.search &&
    left.workflowCode === right.workflowCode &&
    left.lifecycleStatusCode === right.lifecycleStatusCode &&
    left.activationPostureCode === right.activationPostureCode &&
    left.includeHidden === right.includeHidden &&
    left.pageNumber === right.pageNumber &&
    left.pageSize === right.pageSize
  );
}

function toWorkflowSelection(
  definition:
    | WorkflowDefinitionVersionSummary
    | WorkflowDefinitionLifecycleDetail,
): WorkflowDefinitionSelection {
  return {
    workflowCode: definition.workflowCode,
    definitionVersionCode: definition.definitionVersionCode,
  };
}

function formatDateTimeLabel(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function toOptionalNumber(
  value: number | string | null | undefined,
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toPositiveInteger(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

function readPositiveInteger(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}
